import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  SvgIcon,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { withStyles } from 'tss-react/mui';
import LinkIcon from '@mui/icons-material/Link';
import AutocompleteFieldFormik from 'src/components/AutocompleteFieldFormik';
import Tooltip from 'src/components/Tooltip';
import { getActiveSmartLinks, clearActiveSmartLinks } from 'src/actions/smartLinksActions';
import helpers from 'src/helpers';

import selectDynamicFieldStyle from 'src/components/SelectDynamicField/selectDynamicField.style';

// Inline styles — duplicated from selectDynamicFieldStyle so production builds
// are immune to tss-react / MUI CSS injection order issues.
const INLINE = {
  root: {
    position: 'relative',
    display: 'flex',
    border: '1px solid #C7C7C7',
    width: 32,
    height: 32,
    boxSizing: 'border-box',
    padding: 0,
    backgroundColor: '#ebebeb',
    borderRadius: 4,
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  divBtn: {
    display: 'flex',
    width: 35,
    height: 50,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropdown: {
    position: 'absolute',
    display: 'block',
    top: 35,
    left: -356,
    width: 390,
    height: 'auto',
    padding: '20px 21px',
    backgroundColor: '#ffffff',
    borderRadius: 4,
    border: '1px solid #C7C7C7',
    zIndex: 250,
    cursor: 'default',
  },
  buttonLine: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingBottom: 13,
    borderBottom: '1px solid #E0E0E0',
  },
  btnPrimary: {
    color: '#ffffff',
    background: '#5850EC',
    boxShadow: '0px 0px 0px 1px #5850EC',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: 400,
    padding: '5px 20px',
    marginLeft: 8,
  },
  btnCancel: {
    color: '#ffffff',
    background: '#ABABB7',
    boxShadow: '0px 0px 0px 1px #ABABB7',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: 400,
    padding: '5px 20px',
    marginLeft: 8,
  },
  formBox: {
    paddingTop: 12,
  },
  groupLabel: {
    color: '#393939',
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '16px',
    padding: '0px 0px 10px 0px',
  },
  optionValue: {
    color: '#5850EC',
    fontFamily: 'Roboto',
    fontSize: '12px',
  },
};

function SelectSmartLink({
  classes,
  addField,
  externalOpen = null,
  onExternalOpenHandled = () => {},
}) {
  const [openSelect, setOpenSelect] = useState(false);
  const dispatch = useDispatch();
  const elRef = useRef(null);

  const { activeSmartLinks } = useSelector((state) => state?.smartLinks || {});
  const { data: smartLinksData, loading } = activeSmartLinks || {};

  const smartLinkOptions = smartLinksData && smartLinksData.map((link) => ({
    value: link?.id,
    label: link?.name,
    code: link?.code,
    description: link?.description,
  }));

  const initialValues = {
    selectedSmartLink: null,
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
  });

  const {
    errors,
    handleBlur,
    handleChange,
    touched,
    values,
    setFieldTouched,
    setFieldValue,
    resetForm,
  } = formik;

  const shouldFieldBeClose = (e) => (
    !helpers.hasParentClass(e.target, '.MuiAutocomplete-popper')
    && !helpers.hasParentClass(e.target, '.MuiChip-deleteIcon')
  );

  const handleClickAnywhere = (e) => {
    if (elRef?.current && !elRef.current.contains(e.target) && shouldFieldBeClose(e)) {
      setOpenSelect(false);
      document.removeEventListener('click', handleClickAnywhere);
    }
  };

  const renderOptionAutocomplete = (props, option) => (
    <Box
      {...props}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
      }}
    >
      <Box>
        <b>{option.label}</b>
        {option.description && (
          <Box style={INLINE.optionValue}>
            {option.description}
          </Box>
        )}
      </Box>
    </Box>
  );

  const handleFocusFieldValidate = (event) => {
    helpers.formValidation.validateField(event, setFieldTouched);
  };

  const handleOnClickApply = () => {
    // Use Smart Link name for display format (will be converted to code on save)
    const smartLinkName = values.selectedSmartLink?.label || null;
    const dynamicSmartLinkToken = smartLinkName ? `[sl:${smartLinkName}]` : null;

    if (dynamicSmartLinkToken) {
      addField(dynamicSmartLinkToken);
    }
    setOpenSelect(false);
    resetForm();
  };

  const handleOnClickCancel = () => {
    resetForm();
  };

  const handleClick = () => {
    setOpenSelect(!openSelect);
    if (!openSelect) {
      document.addEventListener('click', handleClickAnywhere);
      return;
    }
    document.removeEventListener('click', handleClickAnywhere);
  };

  // Allow parent to programmatically open this picker
  useEffect(() => {
    if (externalOpen && !openSelect) {
      // Defer so the menu's click event finishes propagating before
      // we add the document click listener inside handleClick()
      setTimeout(() => {
        handleClick();
      }, 0);
      onExternalOpenHandled();
    }
  }, [externalOpen]);

  const handleClickApply = () => {
    handleOnClickApply();
  };

  const handleClickCancel = () => {
    handleOnClickCancel();
    setOpenSelect(false);
  };

  useEffect(() => () => document.removeEventListener('click', handleClickAnywhere), []);

  useEffect(() => {
    dispatch(getActiveSmartLinks());
    return () => {
      dispatch(clearActiveSmartLinks());
    };
  }, []);

  const tooltipText = 'Insert a Smart Link';
  const selectText = 'Select Smart Link';

  return (
    <div
      className={`${classes.root} withBtn inner`}
      ref={elRef}
      style={externalOpen !== null ? {
        ...INLINE.root,
        width: 0,
        height: 0,
        border: 'none',
        overflow: 'visible',
        backgroundColor: 'transparent',
      } : INLINE.root}
    >
      {externalOpen === null && (
        <Tooltip
          title={tooltipText}
          placement="left-end"
        >
          <div
            className="divBtn"
            style={INLINE.divBtn}
            onClick={handleClick}
          >
            <SvgIcon
              component={LinkIcon}
            />
          </div>
        </Tooltip>
      )}
      {openSelect && (
        <div className={classes.innerFieldDropDown} style={INLINE.dropdown}>
          <div style={INLINE.buttonLine}>
            <Button
              style={INLINE.btnCancel}
              onClick={handleClickCancel}
            >
              Cancel
            </Button>
            <Button
              style={INLINE.btnPrimary}
              onClick={handleClickApply}
            >
              Insert
            </Button>
          </div>
          <div style={INLINE.formBox}>
            <Grid>
              <div style={INLINE.groupLabel}>
                {selectText}
              </div>
            </Grid>
            <Grid
              spacing={2}
              container
            >
              <Grid
                xs={12}
              >
                <AutocompleteFieldFormik
                  touchedValue={touched.selectedSmartLink}
                  errorValue={errors.selectedSmartLink}
                  name="selectedSmartLink"
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  handleFocus={handleFocusFieldValidate}
                  values={values.selectedSmartLink}
                  loading={loading}
                  label="Select Smart Link"
                  setFieldValue={setFieldValue}
                  options={smartLinkOptions || []}
                  renderOption={renderOptionAutocomplete}
                  isOptionEqualToValue={helpers.isOptionEqualToValue}
                  multiple={false}
                  disableCloseOnSelect={false}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                />
              </Grid>
            </Grid>
          </div>
        </div>
      )}
    </div>
  );
}

SelectSmartLink.propTypes = {
  classes: PropTypes.object.isRequired,
  addField: PropTypes.func.isRequired,
  externalOpen: PropTypes.bool,
  onExternalOpenHandled: PropTypes.func,
};

export default withStyles(SelectSmartLink, selectDynamicFieldStyle, { withTheme: true });
